(function () {
  const API_BASE = 'https://storefront-api.loox.io/storefront/v1/store';
  const TAB_PREVIEW_LIMIT = 2;
  const DEFAULT_DRAWER_PAGE_SIZE = 20;

  function resolveSortParams(sortSetting) {
    if (sortSetting === 'newest') {
      return { sort: 'date', direction: 'desc' };
    }
    return { sort: 'rating', direction: 'desc' };
  }

  function resolvePageSize(value) {
    const size = parseInt(value, 10);
    if (!Number.isFinite(size)) return DEFAULT_DRAWER_PAGE_SIZE;
    return Math.min(50, Math.max(1, size));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starsHtml(rating) {
    const filled = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    let html = '<span class="product-reviews__stars" aria-hidden="true">';
    for (let i = 1; i <= 5; i++) {
      html +=
        '<span class="product-reviews__star' +
        (i <= filled ? ' is-filled' : '') +
        '">★</span>';
    }
    html += '</span>';
    return html;
  }

  function summaryHtml(averageRating, totalReviews) {
    const avg = Number(averageRating || 0).toFixed(1);
    const total = Number(totalReviews || 0);
    const label = total === 1 ? 'REVIEW' : 'REVIEWS';
    return (
      '<span class="product-reviews__summary-star" aria-hidden="true">★</span>' +
      '<span class="product-reviews__summary-text">' +
      escapeHtml(avg) +
      ' BASED ON ' +
      escapeHtml(String(total)) +
      ' ' +
      label +
      '</span>'
    );
  }

  function reviewMediaItems(review) {
    const media = Array.isArray(review.media) ? review.media : [];
    return media.filter((item) => item && item.url);
  }

  function isEmbedVideo(url) {
    return /videodelivery\.net|youtube\.com|youtu\.be|vimeo\.com/i.test(url || '');
  }

  function isVideoMedia(item) {
    if (!item) return false;
    if (item.type === 'video') return true;
    if (isEmbedVideo(item.url)) return true;
    return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(item.url || '');
  }

  function serializeMediaItems(items) {
    return items.map((item) => ({
      type: isVideoMedia(item) ? 'video' : 'image',
      url: item.url,
      thumbnailUrl: item.thumbnailUrl || '',
    }));
  }

  function mediaThumbHtml(review) {
    const items = reviewMediaItems(review);
    if (!items.length) return '';

    const payload = escapeHtml(encodeURIComponent(JSON.stringify(serializeMediaItems(items))));
    return (
      '<div class="product-reviews__media-list" data-reviews-media="' +
      payload +
      '">' +
      items
        .map((item, index) => {
          const video = isVideoMedia(item);
          const src = item.thumbnailUrl || item.url;
          return (
            '<button type="button" class="product-reviews__media" data-reviews-lightbox-open data-reviews-media-index="' +
            index +
            '" aria-label="View review image">' +
            '<img src="' +
            escapeHtml(src) +
            '" alt="" loading="lazy" width="72" height="72">' +
            (video ? '<span class="product-reviews__media-play" aria-hidden="true"></span>' : '') +
            '</button>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  function replyHtml(review, shopName) {
    if (!review.reply || !review.reply.body) return '';
    const brand = shopName || 'Naked Wardrobe';
    return (
      '<div class="product-reviews__reply">' +
      '<button type="button" class="product-reviews__reply-toggle" data-reviews-reply-toggle aria-expanded="false">' +
      '<span class="product-reviews__reply-prefix" aria-hidden="true">+</span> ' +
      escapeHtml(brand) +
      ' Replied' +
      '</button>' +
      '<div class="product-reviews__reply-body" hidden>' +
      '<div class="product-reviews__reply-text">' +
      escapeHtml(review.reply.body) +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function reviewCardHtml(review, options) {
    const opts = options || {};
    const name = (review.reviewer && review.reviewer.name) || 'Customer';
    const productName =
      opts.showProductName && review.product && review.product.name
        ? '<span class="product-reviews__product-name">' +
          escapeHtml(review.product.name) +
          '</span>'
        : '';
    const verified =
      review.verified === true
        ? '<span class="product-reviews__verified" title="Verified purchase" aria-label="Verified purchase">✓</span>'
        : '';
    const body = review.body
      ? '<div class="product-reviews__body">' + escapeHtml(review.body) + '</div>'
      : '';

    return (
      '<article class="product-reviews__card" data-review-id="' +
      escapeHtml(review.id || '') +
      '">' +
      '<div class="product-reviews__card-main">' +
      '<div class="product-reviews__meta">' +
      '<div class="product-reviews__identity">' +
      '<span class="product-reviews__name">' +
      escapeHtml(name) +
      '</span>' +
      verified +
      '</div>' +
      '<div class="product-reviews__rating-row">' +
      starsHtml(review.rating) +
      productName +
      '</div>' +
      '</div>' +
      body +
      replyHtml(review, opts.shopName) +
      '</div>' +
      mediaThumbHtml(review) +
      '</article>'
    );
  }

  function setReplyExpanded(toggle, expanded) {
    const wrap = toggle.closest('.product-reviews__reply');
    const body = wrap && wrap.querySelector('.product-reviews__reply-body');
    const prefix = toggle.querySelector('.product-reviews__reply-prefix');
    if (!body) return;
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (prefix) prefix.textContent = expanded ? '-' : '+';
    if (expanded) body.removeAttribute('hidden');
    else body.setAttribute('hidden', '');
  }

  async function fetchJson(url) {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error('Loox request failed: ' + response.status);
    }
    return response.json();
  }

  function buildReviewsUrl(storeId, params) {
    const url = new URL(API_BASE + '/' + encodeURIComponent(storeId) + '/product-reviews');
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        url.searchParams.set(key, params[key]);
      }
    });
    return url.toString();
  }

  function buildBottomlineUrl(storeId, productId) {
    return (
      API_BASE +
      '/' +
      encodeURIComponent(storeId) +
      '/product-reviews/bottomline/' +
      encodeURIComponent(productId)
    );
  }

  class ProductReviewsDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.overlay = this.querySelector('[data-reviews-drawer-overlay]');
      this.titleEl = this.querySelector('[data-reviews-drawer-title]');
      this.summaryEl = this.querySelector('[data-reviews-drawer-summary]');
      this.listEl = this.querySelector('[data-reviews-drawer-list]');
      this.loadingEl = this.querySelector('[data-reviews-drawer-loading]');
      this.errorEl = this.querySelector('[data-reviews-drawer-error]');
      this.loadMoreBtn = this.querySelector('[data-reviews-drawer-load-more]');
      this.storeId = this.getAttribute('data-store-id') || '';
      this.shopName = this.getAttribute('data-shop-name') || '';
      this.sortSetting = this.getAttribute('data-sort') || 'rating';
      this.pageSize = resolvePageSize(this.getAttribute('data-page-size'));

      this.mode = 'product';
      this.productId = '';
      this.page = 1;
      this.total = null;
      this.hasMore = false;
      this.loading = false;

      this.querySelectorAll('[data-reviews-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () => this.loadMore());
      }

      this.listEl &&
        this.listEl.addEventListener('click', (event) => {
          const toggle = event.target.closest('[data-reviews-reply-toggle]');
          if (!toggle) return;
          const open = toggle.getAttribute('aria-expanded') === 'true';
          setReplyExpanded(toggle, !open);
        });

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.classList.contains('active')) return;
          if (document.querySelector('dialog.product-reviews-lightbox[open]')) return;
          event.preventDefault();
          event.stopPropagation();
          this._suppressEscape = true;
          this.close();
        },
        true
      );
      document.addEventListener(
        'keyup',
        (event) => {
          if (event.key !== 'Escape' || !this._suppressEscape) return;
          event.preventDefault();
          event.stopPropagation();
          this._suppressEscape = false;
        },
        true
      );
    }

    setTitle(total) {
      if (!this.titleEl) return;
      const count = Number(total);
      if (Number.isFinite(count) && count > 0) {
        this.titleEl.textContent = 'Reviews (' + count + ')';
      } else {
        this.titleEl.textContent = 'Reviews';
      }
    }

    open(config) {
      const opts = config || {};
      this.mode = opts.mode || 'product';
      this.productId = opts.productId || '';
      this.page = 1;
      this.total = opts.totalCount != null ? Number(opts.totalCount) : null;
      this.hasMore = false;
      if (this.listEl) this.listEl.innerHTML = '';
      if (this.errorEl) this.errorEl.setAttribute('hidden', '');
      this.setTitle(this.total);
      this.updateLoadMore();

      if (this.summaryEl) {
        if (opts.summaryHtml) {
          this.summaryEl.innerHTML = opts.summaryHtml;
          this.summaryEl.removeAttribute('hidden');
        } else {
          this.summaryEl.innerHTML = '';
          this.summaryEl.setAttribute('hidden', '');
        }
      }

      this.classList.add('active');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.fetchPage(true);
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      const cartStillOpen = document.querySelector('cart-drawer.active');
      const sizeGuideOpen = document.querySelector('size-guide-drawer.active');
      if (!cartStillOpen && !sizeGuideOpen) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      if (this.loadingEl) {
        if (isLoading) this.loadingEl.removeAttribute('hidden');
        else this.loadingEl.setAttribute('hidden', '');
      }
      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = isLoading;
      }
    }

    async fetchPage(replace) {
      if (!this.storeId || this.loading) return;
      this.setLoading(true);
      if (this.errorEl) this.errorEl.setAttribute('hidden', '');

      try {
        const sortParams = resolveSortParams(this.sortSetting);
        const params = {
          sort: sortParams.sort,
          direction: sortParams.direction,
          page: this.page,
          limit: this.pageSize,
        };
        if (this.mode === 'product' && this.productId) {
          params.product_id = this.productId;
        }
        if (this.total != null) params.total = this.total;

        const data = await fetchJson(buildReviewsUrl(this.storeId, params));
        const reviews = Array.isArray(data.reviews) ? data.reviews : [];
        const pagination = data.pagination || {};
        this.total = pagination.total != null ? pagination.total : this.total;
        this.renderReviews(reviews, replace);
        this.hasMore = this.computeHasMore(reviews, pagination);
        this.setTitle(this.total);
        this.updateLoadMore();
      } catch (error) {
        console.error(error);
        this.hasMore = false;
        this.updateLoadMore();
        if (this.errorEl) this.errorEl.removeAttribute('hidden');
      } finally {
        this.setLoading(false);
      }
    }

    computeHasMore(reviews, pagination) {
      if (!reviews.length) return false;

      const page = pagination || {};

      // Loox signals the end of the list with nextPageUrl: null.
      if (page.nextPageUrl == null || page.nextPageUrl === '') return false;
      if (page.hasMore === false) return false;
      if (reviews.length < this.pageSize) return false;

      const rendered = this.listEl
        ? this.listEl.querySelectorAll('.product-reviews__card').length
        : reviews.length;
      if (this.total != null && rendered >= Number(this.total)) return false;

      return page.hasMore === true || Boolean(page.nextPageUrl);
    }

    renderReviews(reviews, replace) {
      if (!this.listEl) return;
      const html = reviews
        .map((review) =>
          reviewCardHtml(review, {
            showProductName: this.mode === 'store',
            shopName: this.shopName,
          })
        )
        .join('');
      if (replace) this.listEl.innerHTML = html;
      else this.listEl.insertAdjacentHTML('beforeend', html);
    }

    updateLoadMore() {
      if (!this.loadMoreBtn) return;
      if (this.hasMore) this.loadMoreBtn.removeAttribute('hidden');
      else this.loadMoreBtn.setAttribute('hidden', '');
    }

    loadMore() {
      if (!this.hasMore || this.loading) return;
      this.page += 1;
      this.fetchPage(false);
    }
  }

  class ProductReviewsController {
    constructor() {
      this.tabs = Array.from(document.querySelectorAll('[data-product-reviews-tab]'));
      this.drawer = document.querySelector('product-reviews-drawer');
      this.loaded = false;
      this.loading = false;
      this.mode = 'product';
      this.bottomline = null;
      this.previewReviews = [];
    }

    init() {
      if (!this.tabs.length) return;

      const sample = this.tabs[0];
      this.storeId = sample.getAttribute('data-store-id') || '';
      this.productId = sample.getAttribute('data-product-id') || '';
      this.shopName = sample.getAttribute('data-shop-name') || '';
      this.sortSetting =
        sample.getAttribute('data-sort') ||
        (this.drawer && this.drawer.getAttribute('data-sort')) ||
        'rating';
      if (this.drawer) this.drawer.sortSetting = this.sortSetting;
      if (!this.storeId || !this.productId) return;

      this.tabs.forEach((tabRoot) => {
        const viewAll = tabRoot.querySelector('[data-reviews-view-all]');
        if (viewAll) {
          viewAll.addEventListener('click', () => this.openDrawer());
        }
        tabRoot.addEventListener('click', (event) => {
          const toggle = event.target.closest('[data-reviews-reply-toggle]');
          if (!toggle) return;
          const open = toggle.getAttribute('aria-expanded') === 'true';
          setReplyExpanded(toggle, !open);
        });
      });

      document.querySelectorAll('[data-type="reviews"]').forEach((tab) => {
        tab.addEventListener('click', () => {
          this.ensureLoaded();
        });
        if (tab.classList.contains('active')) {
          this.ensureLoaded();
        }
      });
    }

    setTabState(key, visible) {
      this.tabs.forEach((tabRoot) => {
        const el = tabRoot.querySelector('[data-reviews-' + key + ']');
        if (!el) return;
        if (visible) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.setTabState('loading', isLoading);
    }

    async ensureLoaded() {
      if (this.loaded || this.loading) return;
      this.setLoading(true);
      this.setTabState('error', false);

      try {
        this.bottomline = await fetchJson(buildBottomlineUrl(this.storeId, this.productId));
        const total = Number(this.bottomline.totalReviews || 0);

        const sortParams = resolveSortParams(this.sortSetting);

        if (total > 0) {
          this.mode = 'product';
          const data = await fetchJson(
            buildReviewsUrl(this.storeId, {
              product_id: this.productId,
              sort: sortParams.sort,
              direction: sortParams.direction,
              page: 1,
              limit: TAB_PREVIEW_LIMIT,
              total: total,
            })
          );
          this.previewReviews = Array.isArray(data.reviews) ? data.reviews : [];
        } else {
          this.mode = 'store';
          const data = await fetchJson(
            buildReviewsUrl(this.storeId, {
              sort: sortParams.sort,
              direction: sortParams.direction,
              page: 1,
              limit: TAB_PREVIEW_LIMIT,
            })
          );
          this.previewReviews = Array.isArray(data.reviews) ? data.reviews : [];
        }

        this.renderTabs();
        this.loaded = true;
      } catch (error) {
        console.error(error);
        this.setTabState('error', true);
      } finally {
        this.setLoading(false);
      }
    }

    renderTabs() {
      const showSummary = this.mode === 'product' && this.bottomline;
      const showEmptyMessage = this.mode === 'store';
      const showProductName = this.mode === 'store';
      const hasReviews = this.previewReviews.length > 0;

      this.tabs.forEach((tabRoot) => {
        const summaryEl = tabRoot.querySelector('[data-reviews-summary]');
        const emptyEl = tabRoot.querySelector('[data-reviews-empty-message]');
        const listEl = tabRoot.querySelector('[data-reviews-list]');
        const viewAll = tabRoot.querySelector('[data-reviews-view-all]');

        if (summaryEl) {
          if (showSummary) {
            summaryEl.innerHTML = summaryHtml(
              this.bottomline.averageRating,
              this.bottomline.totalReviews
            );
            summaryEl.removeAttribute('hidden');
          } else {
            summaryEl.innerHTML = '';
            summaryEl.setAttribute('hidden', '');
          }
        }

        if (emptyEl) {
          if (showEmptyMessage) emptyEl.removeAttribute('hidden');
          else emptyEl.setAttribute('hidden', '');
        }

        if (listEl) {
          listEl.innerHTML = this.previewReviews
            .map((review) =>
              reviewCardHtml(review, {
                showProductName: showProductName,
                shopName: this.shopName,
              })
            )
            .join('');
        }

        if (viewAll) {
          if (hasReviews) viewAll.removeAttribute('hidden');
          else viewAll.setAttribute('hidden', '');
        }
      });
    }

    openDrawer() {
      if (!this.drawer || typeof this.drawer.open !== 'function') return;
      const summary =
        this.mode === 'product' && this.bottomline
          ? summaryHtml(this.bottomline.averageRating, this.bottomline.totalReviews)
          : '';
      const totalCount =
        this.mode === 'product' && this.bottomline
          ? Number(this.bottomline.totalReviews || 0)
          : null;
      this.drawer.open({
        mode: this.mode,
        productId: this.productId,
        summaryHtml: summary,
        totalCount: totalCount,
      });
    }
  }

  class ReviewsLightbox {
    constructor(dialog) {
      this.dialog = dialog;
      this.stage = dialog.querySelector('[data-reviews-lightbox-stage]');
      this.prevBtn = dialog.querySelector('[data-reviews-lightbox-prev]');
      this.nextBtn = dialog.querySelector('[data-reviews-lightbox-next]');
      this.items = [];
      this.index = 0;

      dialog.querySelectorAll('[data-reviews-lightbox-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.show(this.index - 1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.show(this.index + 1);
        });
      }

      dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      dialog.addEventListener('close', () => {
        if (this.stage) this.stage.innerHTML = '';
        this.items = [];
      });
      dialog.addEventListener('keydown', (event) => {
        if (!this.dialog.open) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + 1);
        }
      });

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-reviews-lightbox-open]');
        if (!opener) return;
        const list = opener.closest('[data-reviews-media]');
        if (!list) return;
        event.preventDefault();
        let items = [];
        try {
          items = JSON.parse(decodeURIComponent(list.getAttribute('data-reviews-media') || '%5B%5D'));
        } catch (error) {
          return;
        }
        const index = parseInt(opener.getAttribute('data-reviews-media-index'), 10) || 0;
        this.open(items, index);
      });
    }

    open(items, index) {
      this.items = Array.isArray(items) ? items.filter((item) => item && item.url) : [];
      if (!this.items.length) return;
      this.show(index);
      if (!this.dialog.open) this.dialog.showModal();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    show(index) {
      if (!this.items.length || !this.stage) return;
      const len = this.items.length;
      this.index = ((index % len) + len) % len;
      const item = this.items[this.index];
      if (isEmbedVideo(item.url)) {
        this.stage.innerHTML =
          '<iframe class="product-reviews-lightbox__media product-reviews-lightbox__media--embed" src="' +
          escapeHtml(item.url) +
          '" title="Review video" allow="autoplay; fullscreen" allowfullscreen></iframe>';
      } else if (isVideoMedia(item)) {
        this.stage.innerHTML =
          '<video class="product-reviews-lightbox__media" src="' +
          escapeHtml(item.url) +
          '" controls autoplay playsinline></video>';
      } else {
        this.stage.innerHTML =
          '<button type="button" class="product-reviews-lightbox__image-btn" data-reviews-lightbox-image aria-label="Review photo">' +
          '<img class="product-reviews-lightbox__media" src="' +
          escapeHtml(item.url) +
          '" alt="Review photo">' +
          '</button>';
      }

      const multi = len > 1;
      if (this.prevBtn) {
        if (multi) this.prevBtn.removeAttribute('hidden');
        else this.prevBtn.setAttribute('hidden', '');
      }
      if (this.nextBtn) {
        if (multi) this.nextBtn.removeAttribute('hidden');
        else this.nextBtn.setAttribute('hidden', '');
      }
    }
  }

  if (!customElements.get('product-reviews-drawer')) {
    customElements.define('product-reviews-drawer', ProductReviewsDrawer);
  }

  function bootProductReviews() {
    if (window.__productReviewsInitialized) return;
    window.__productReviewsInitialized = true;
    const lightboxEl = document.querySelector('[data-reviews-lightbox]');
    if (lightboxEl) {
      const lightbox = new ReviewsLightbox(lightboxEl);
      lightboxEl.addEventListener('click', (event) => {
        const imageBtn = event.target.closest('[data-reviews-lightbox-image]');
        if (!imageBtn) return;
        if (lightbox.items.length > 1) lightbox.show(lightbox.index + 1);
        else lightbox.close();
      });
    }
    const controller = new ProductReviewsController();
    controller.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootProductReviews);
  } else {
    bootProductReviews();
  }
})();
